// Client Types
export type ClientStatus = 'active' | 'at-risk' | 'churned' | 'pending-migration';
export type PaymentStatus = 'current' | 'flagged' | 'suspended';

export interface ClientContact {
  name: string;
  email: string;
  phone: string;
}

export interface Client {
  id: string;
  partitionKey: 'client';
  pax8CompanyId: string | null;
  companyName: string;
  pax8CompanyExternalId: string | null;
  tenantId: string | null;
  tenantDomain?: string | null;  // Cached tenant domain (e.g., contoso.onmicrosoft.com)
  workspaceType?: 'm365' | 'google' | 'hybrid';  // Which platform this client uses
  googleAdminEmail?: string | null;  // Google Workspace super admin email for API access
  googleDomain?: string | null;  // Primary Google Workspace domain
  status: ClientStatus;
  paymentStatus: PaymentStatus;
  paymentFlaggedDate: string | null;
  notes: string;
  contacts: ClientContact[];
  createdDate: string;
  modifiedDate: string;
  modifiedBy: string;
  subscriptions?: Subscription[];
  // Licenses to ignore in coverage calculations (purchased from other vendors)
  ignoredLicenses?: string[];  // Array of normalized product names to ignore
}

// Subscription Types
export type BillingTerm = 'annual' | 'monthly';
export type SubscriptionStatus = 'active' | 'suspended' | 'cancelled' | 'pending-cancellation';
export type SubscriptionSource = 'pax8' | 'direct' | 'manual';
export type SubscriptionCategory = 'paid' | 'trial' | 'free';

// Known free/trial SKU patterns (Microsoft products that don't have "free" or "trial" in name)
// These patterns are ONLY checked for MS Direct subscriptions, not Pax8 (Pax8 sells these as paid)
const FREE_SKU_PATTERNS = [
  // Trial products (often have huge license counts like 1M or 10K)
  'forms_pro',                  // Dynamics 365 Customer Voice Trial
  'dyn365_enterprise_p1_iw',    // Dynamics 365 P1 Trial for Information Workers
  'dyn365_enterprise_plan1',    // Dynamics 365 Plan 1 Trial
  'powerapps_per_user',         // Power Apps trial
  'power_bi_pro',               // Often trial
  'projectprofessional',        // Project trial
  'visioclient',                // Visio trial
  // Free/viral products
  'flow_free',                  // Microsoft Power Automate Free
  'teams_exploratory',
  'teams exploratory',
  'power_apps_per_user_plan',   // Developer plan
  'powerapps_dev',
  'powerapps_viral',
  'flow_p2_viral',
  'flow_viral',
  'powerautomate_viral',
  'rightsmanagement_adhoc',
  'rms_s_basic',
  'projectmadeira_viral',
  'deskless',
  'ccibots_privprev',
  'exchange_s_foundation',
  'mcostandard_virtual',
  'windows_store',
  'stream_p1',
  'stream_p2',
  'microsoft_search',
  'minecraft_education',
  'crm_online_portal',
  'customer_voice',             // Customer Voice trials
  'd365_sales',                 // D365 Sales trials
  'power automate',             // Catch Power Automate variations
  // Entra ID / Azure AD Premium - free when bundled from Microsoft
  // Pax8 sells these as paid products, so only match for direct subscriptions
  'azure ad premium',           // Legacy Azure AD Premium P1/P2 names
  'azure active directory premium',
  'aad_premium',                // SKU names
  'aadpremium',
  'entra id p1',                // Modern Entra ID names (free when direct, paid when Pax8)
  'entra id p2',
];

// Helper to detect trial/free subscriptions
export function getSubscriptionCategory(sub: { sku: string; productName: string; totalPrice: number; source?: SubscriptionSource; quantity?: number }): SubscriptionCategory {
  const skuLower = sub.sku.toLowerCase();
  const nameLower = sub.productName.toLowerCase();

  // Check for trial patterns in SKU or name
  if (skuLower.includes('trial') || nameLower.includes('trial')) {
    return 'trial';
  }

  // Check for free patterns in SKU or name
  if (skuLower.includes('free') || nameLower.includes('free')) {
    return 'free';
  }

  // Check for known free/trial SKU patterns - ONLY for MS Direct subscriptions
  // Pax8 sells these as paid products, so don't mark Pax8 subs as free/trial
  if (sub.source !== 'pax8') {
    for (const pattern of FREE_SKU_PATTERNS) {
      if (skuLower.includes(pattern) || nameLower.includes(pattern)) {
        return 'trial';
      }
    }
  }

  // Check for exploratory/viral/developer keywords (common in free Microsoft licenses)
  if (skuLower.includes('exploratory') || nameLower.includes('exploratory') ||
      skuLower.includes('viral') || nameLower.includes('viral') ||
      skuLower.includes('_dev') || nameLower.includes('developer')) {
    return 'free';
  }

  // Heuristic: Direct subscriptions with huge license counts (10K+) are almost always trials
  // Microsoft trials typically come with 10,000 or 1,000,000 licenses
  if (sub.source === 'direct' && sub.quantity && sub.quantity >= 10000) {
    return 'trial';
  }

  // Only check price for Pax8 subscriptions (Direct subs have $0 price but aren't free)
  if (sub.source === 'pax8' && sub.totalPrice === 0) {
    return 'free';
  }

  return 'paid';
}

export interface Subscription {
  id: string;
  partitionKey: 'subscription';
  clientId: string;
  clientName?: string;
  pax8SubscriptionId: string | null;
  productName: string;
  sku: string;
  quantity: number;
  assignedQuantity: number | null;
  billingTerm: BillingTerm;
  commitmentTerm?: string;  // e.g., "1-Year", "Monthly" - the commitment period
  commitmentEndDate: string;
  renewalDate: string;
  autoRenew: boolean;
  status: SubscriptionStatus;
  futureCancellationSubmitted: boolean;
  futureCancellationDate: string | null;
  futureCancellationVerified: boolean;
  futureCancellationVerifiedDate: string | null;
  source: SubscriptionSource;
  unitPrice: number;
  totalPrice: number;
  lastSyncDate: string | null;
  createdDate: string;
  modifiedDate: string;
  // MS Billing fields (from /directory/subscriptions via OAuth)
  msBillingStatus?: 'active' | 'warning' | 'suspended' | 'unknown';
  msNextLifecycleDate?: string | null;  // Calculated renewal date (anniversary of msCreatedDate)
  msCreatedDate?: string | null;         // Original subscription purchase date from MS
  msIsTrial?: boolean;
  msCommerceSubscriptionId?: string;
  billingLastChecked?: string | null;
  // MS SKU matching fields
  msSkuPartNumber?: string;  // e.g., "O365_BUSINESS_ESSENTIALS"
  msSkuId?: string;          // MS SKU GUID (for license assignment)
}

// Alert Types
export type AlertType = 'renewal' | 'payment' | 'utilization' | 'cancellation' | 'migration';
export type AlertSeverity = 'critical' | 'warning' | 'info';
export type AlertStatus = 'active' | 'acknowledged' | 'resolved' | 'snoozed';

export interface Alert {
  id: string;
  partitionKey: 'alert';
  clientId: string;
  clientName?: string;
  subscriptionId: string | null;
  alertType: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  actionRequired: string;
  status: AlertStatus;
  snoozedUntil: string | null;
  createdDate: string;
  acknowledgedDate: string | null;
  acknowledgedBy: string | null;
  resolvedDate: string | null;
  resolvedBy: string | null;
}

// Dashboard Stats
export interface DashboardStats {
  totalActiveSubscriptions: number;
  renewalsNext30Days: number;
  atRiskClientsCount: number;
  pendingCancellations: number;
  totalLicenses: number;
  monthlyRecurringRevenue: number;
  totalClients: number;
}

export interface SyncStatus {
  pax8: {
    lastSync: string;
    status: string;
    clientsProcessed: number;
    subscriptionsProcessed: number;
  } | null;
  gdap: {
    lastSync: string;
    status: string;
    clientsProcessed: number;
  } | null;
}

export interface DashboardData {
  stats: DashboardStats;
  recentAlerts: Alert[];
  upcomingRenewals: Subscription[];
  syncStatus: SyncStatus;
}

// API Response Types
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Form Types
export interface CreateClientRequest {
  companyName: string;
  pax8CompanyId?: string;
  pax8CompanyExternalId?: string;
  tenantId?: string;
  status?: ClientStatus;
  paymentStatus?: PaymentStatus;
  notes?: string;
  contacts?: ClientContact[];
  workspaceType?: 'm365' | 'google' | 'hybrid';
  googleAdminEmail?: string;
  googleDomain?: string;
}

export interface UpdateClientRequest {
  companyName?: string;
  tenantId?: string;
  status?: ClientStatus;
  paymentStatus?: PaymentStatus;
  paymentFlaggedDate?: string | null;
  notes?: string;
  contacts?: ClientContact[];
  ignoredLicenses?: string[];  // Licenses to ignore in coverage (from other vendors)
  workspaceType?: 'm365' | 'google' | 'hybrid';
  googleAdminEmail?: string | null;
  googleDomain?: string | null;
}

export interface CreateSubscriptionRequest {
  clientId: string;
  productName: string;
  sku: string;
  quantity: number;
  billingTerm: BillingTerm;
  commitmentEndDate: string;
  renewalDate: string;
  autoRenew?: boolean;
  status?: SubscriptionStatus;
  source?: SubscriptionSource;
  unitPrice?: number;
  totalPrice?: number;
}

export interface UpdateSubscriptionRequest {
  quantity?: number;
  autoRenew?: boolean;
  status?: SubscriptionStatus;
  futureCancellationSubmitted?: boolean;
  futureCancellationDate?: string | null;
  futureCancellationVerified?: boolean;
  futureCancellationVerifiedDate?: string | null;
  assignedQuantity?: number | null;
}

export interface UpdateAlertRequest {
  status: AlertStatus;
  snoozedUntil?: string | null;
}

// Audit Log Types
export type AuditAction =
  | 'create' | 'update' | 'delete' | 'sync' | 'flag' | 'resolve'
  | 'run' | 'enable' | 'disable'
  | 'assign-license' | 'remove-license'
  | 'add-to-group' | 'remove-from-group'
  | 'reset-password' | 'offboard'
  | 'login' | 'view' | 'list'
  | 'update-ca-policy' | 'create-ca-policy' | 'delete-ca-policy';

export type EntityType =
  | 'client' | 'subscription' | 'alert'
  | 'report-definition' | 'report-execution'
  | 'user' | 'group'
  | 'session' | 'page'
  | 'conditional-access';

export interface AuditLogChange {
  old: unknown;
  new: unknown;
}

export interface AuditLog {
  id: string;
  partitionKey: 'audit';
  entityType: EntityType;
  entityId: string;
  action: AuditAction;
  userId: string;
  userEmail: string;
  timestamp: string;
  changes: Record<string, AuditLogChange>;
}

export interface AuditLogFilters {
  entityType?: EntityType;
  entityId?: string;
  action?: AuditAction;
  userEmail?: string;
  fromDate?: string;
  toDate?: string;
}

// User Types (from Graph API)
export interface TenantUser {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail: string | null;
  jobTitle: string | null;
  department: string | null;
  assignedLicenses: { skuId: string; skuName: string }[];
  licenseCount: number;
  accountEnabled: boolean;
  createdDateTime: string | null;
  directoryRoles?: { displayName: string }[];
  mfaRegistered?: boolean;
  mfaMethods?: string[];
}

export interface ClientUsersResponse {
  clientId: string;
  clientName: string;
  tenantId: string;
  userCount: number;
  users: TenantUser[];
}

// User Role (from Azure AD)
export interface UserRole {
  id: string;
  displayName: string;
  description: string;
}

// User Group (from Azure AD)
export interface UserGroup {
  id: string;
  displayName: string;
  description: string | null;
  groupType: string; // 'Security', 'Microsoft 365', 'Distribution'
  mailEnabled: boolean;
  securityEnabled: boolean;
  mail: string | null;
}

// Group Member (from Azure AD)
export interface GroupMember {
  id: string;
  displayName: string;
  email: string | null;
  type: 'user' | 'group' | 'device' | 'servicePrincipal' | 'other';
}

// Available License (licenses with remaining capacity)
export interface AvailableLicense {
  skuId: string;
  skuName: string;
  available: number;
  total: number;
}

// Detailed User (for user detail view)
export interface DetailedUser {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail: string | null;
  jobTitle: string | null;
  department: string | null;
  officeLocation: string | null;
  mobilePhone: string | null;
  businessPhones: string[];
  assignedLicenses: { skuId: string; skuName: string }[];
  accountEnabled: boolean;
  createdDateTime: string | null;
  lastPasswordChangeDateTime: string | null;
  signInActivity?: {
    lastSignInDateTime: string | null;
    lastNonInteractiveSignInDateTime: string | null;
  };
  roles: UserRole[];
  groups: UserGroup[];
  availableLicenses: AvailableLicense[];
  clientId: string;
  clientName: string;
  tenantId: string;
  // Mailbox permissions: null = not loaded yet (lazy-load via button)
  mailboxPermissions: unknown | null;
  // Cached sign-in logs from forensic data
  cachedSignIns?: {
    createdDateTime: string;
    userPrincipalName: string;
    ipAddress: string;
    appDisplayName: string;
    clientAppUsed: string;
    status: { errorCode: number; failureReason: string };
    location: { city: string; state: string; countryOrRegion: string };
    riskLevelDuringSignIn: string;
    riskLevelAggregated?: string;
    conditionalAccessStatus?: string;
  }[];
  // Cache timestamps (null = data was fetched live)
  cachedAt?: {
    roles: string | null;
    groups: string | null;
    signIns: string | null;
    forensic: string | null;
  };
}

// Tenant Groups Response
export interface TenantGroupsResponse {
  clientId: string;
  clientName: string;
  tenantId: string;
  groupCount: number;
  groups: UserGroup[];
}

// Password Reset Response
export interface PasswordResetResponse {
  success: boolean;
  message: string;
  temporaryPassword?: string;
  forceChangeOnNextSignIn: boolean;
}

// Generic Action Response
export interface ActionResponse {
  success: boolean;
  message: string;
  error?: string;
}

// Offboarding Types
export interface OffboardingOperations {
  disableAccount?: boolean;
  revokeSession?: boolean;
  resetPassword?: boolean;
  removeLicenses?: boolean;
  removeFromGroups?: boolean;
  convertToShared?: boolean;
  grantMailboxAccess?: boolean;
  hideFromGAL?: boolean;
  shareOneDrive?: boolean;
}

export interface OffboardUserRequest {
  operations: OffboardingOperations;
  delegateEmail?: string;
  delegateEmails?: string[];
  oneDriveAccessLevel?: 'read' | 'write' | 'owner';
}

export interface OffboardingOperationStatus {
  requested: boolean;
  completed: boolean;
  error?: string;
}

export interface OffboardingResponse {
  success: boolean;
  offboardingId: string;
  status: 'pending' | 'in-progress' | 'completed' | 'partial' | 'failed';
  user: {
    id: string;
    displayName: string;
    email: string;
  };
  results: string[];
  errors: string[];
  exchangeOperationsPending: boolean;
  message: string;
}

export interface OffboardingRecord {
  id: string;
  clientId: string;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  delegateEmail: string | null;
  operations: {
    disableAccount: OffboardingOperationStatus;
    revokeSession: OffboardingOperationStatus;
    resetPassword: OffboardingOperationStatus;
    removeLicenses: OffboardingOperationStatus & { licensesRemoved?: string[] };
    removeFromGroups: OffboardingOperationStatus & { groupsRemoved?: number; groupsFailed?: number };
    convertToShared: OffboardingOperationStatus;
    grantMailboxAccess: OffboardingOperationStatus;
    hideFromGAL: OffboardingOperationStatus;
    shareOneDrive: OffboardingOperationStatus & { accessLevel?: string };
  };
  status: 'pending' | 'in-progress' | 'completed' | 'partial' | 'failed';
  startedAt: string;
  completedAt?: string;
  initiatedBy: string;
  initiatedByEmail: string;
}

// ============================================
// Scheduled Reports Types
// ============================================

export type ReportType = 'mfa' | 'license' | 'admin-roles' | 'mailbox-security' | 'email-security' | 'conditional-access';
export type ScheduleFrequency = 'manual' | 'daily' | 'weekly' | 'monthly';
export type ReportExecutionStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface ReportSchedule {
  enabled: boolean;
  frequency: ScheduleFrequency;
  dayOfWeek?: number;
  dayOfMonth?: number;
  time?: string;
}

export interface ReportOptions {
  uploadToITGlue: boolean;
  itglueOrgId?: string;
  emailOnComplete: boolean;
  emailRecipients?: string[];
}

export interface ReportDefinition {
  id: string;
  clientId: string;
  clientName: string;
  reportType: ReportType;
  schedule: ReportSchedule;
  options: ReportOptions;
  lastRunAt?: string;
  lastRunStatus?: ReportExecutionStatus;
  createdAt: string;
  createdBy: string;
  createdByEmail: string;
  modifiedAt?: string;
  latestExecution?: ReportExecution;
}

export interface ReportStats {
  [key: string]: number | string | boolean | undefined;
}

export interface ReportExecution {
  id: string;
  definitionId: string;
  clientId: string;
  clientName: string;
  reportType: ReportType;
  status: ReportExecutionStatus;
  startedAt: string;
  completedAt?: string;
  triggeredBy: 'schedule' | 'manual';
  triggeredByUser?: string;
  triggeredByEmail?: string;
  reportHtml?: string;
  reportUrl?: string;
  pdfUrl?: string;
  itglueAssetId?: string;
  stats?: ReportStats;
  error?: string;
  errorDetails?: string;
}

export interface CreateReportDefinitionRequest {
  clientId: string;
  reportType: ReportType;
  schedule: ReportSchedule;
  options?: Partial<ReportOptions>;
}

export interface UpdateReportDefinitionRequest {
  schedule?: Partial<ReportSchedule>;
  options?: Partial<ReportOptions>;
}

// Report type metadata for UI
export const REPORT_TYPE_INFO: Record<ReportType, { name: string; description: string; icon: string }> = {
  'mfa': {
    name: 'MFA Dashboard',
    description: 'User MFA registration status, admin MFA adoption, security recommendations',
    icon: 'ShieldCheckIcon'
  },
  'license': {
    name: 'License Dashboard',
    description: 'Pax8 purchases vs M365 usage, utilization percentages, cost optimization',
    icon: 'TicketIcon'
  },
  'admin-roles': {
    name: 'Admin Roles Dashboard',
    description: 'Privileged role assignments, inactive admins, security risks',
    icon: 'UserGroupIcon'
  },
  'mailbox-security': {
    name: 'Mailbox Security',
    description: 'Mailbox forwarding rules, delegate access, suspicious configurations',
    icon: 'EnvelopeIcon'
  },
  'email-security': {
    name: 'Email Security',
    description: 'Transport rules, spam policies, anti-phishing configurations',
    icon: 'ShieldExclamationIcon'
  },
  'conditional-access': {
    name: 'Conditional Access',
    description: 'Policy coverage, gaps in protection, compliance status',
    icon: 'KeyIcon'
  }
};

export const SCHEDULE_FREQUENCY_INFO: Record<ScheduleFrequency, string> = {
  'manual': 'Manual only',
  'daily': 'Daily',
  'weekly': 'Weekly',
  'monthly': 'Monthly'
};

export const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ============================================
// Shared Mailbox Types
// ============================================

export interface SharedMailbox {
  id: string;
  displayName: string;
  email: string;
  alias: string;
  memberCount: number;
  members: string[];
  createdDate?: string;
  isAutoMapped?: boolean;
}

export interface CreateSharedMailboxRequest {
  displayName: string;
  emailAddress: string;
  alias?: string;
  owners?: string[];
}

// ============================================
// Mailbox Permissions Types
// ============================================

export interface MailboxDelegate {
  user: string;
  fullAccess: boolean;
  sendAs: boolean;
  sendOnBehalf: boolean;
}

export interface ManageMailboxPermissionRequest {
  delegateEmail: string;
  action: 'add' | 'remove';
  fullAccess?: boolean;
  sendAs?: boolean;
  sendOnBehalf?: boolean;
}

// ============================================
// OneDrive Sharing Types
// ============================================

export interface OneDriveShare {
  id: string;
  userEmail: string;
  userName: string;
  userId?: string;
  accessLevel: 'read' | 'write' | 'owner';
  shareType: 'direct' | 'link';
  isInherited: boolean;
}

export interface GrantOneDriveAccessRequest {
  delegateEmail: string;
  accessLevel: 'read' | 'write' | 'owner';
}

// ============================================
// Client Sync Data Types (cached MS tenant data)
// ============================================

export type SyncCategoryStatus = 'pending' | 'syncing' | 'success' | 'failed' | 'skipped';
export type OverallSyncStatus = 'idle' | 'syncing' | 'partial' | 'complete' | 'failed';
export type Staleness = 'fresh' | 'stale' | 'very_stale' | 'never_synced';

export interface SyncCategory<T> {
  lastSynced: string | null;
  status: SyncCategoryStatus;
  error: string | null;
  count: number;
  data: T[];
}

export interface SyncCategorySingle<T> {
  lastSynced: string | null;
  status: SyncCategoryStatus;
  error: string | null;
  data: T | null;
}

export interface SyncSchedule {
  enabled: boolean;
  intervalHours: number;
  lastScheduledRun: string | null;
  nextScheduledRun: string | null;
}

export interface SyncedUser {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail: string | null;
  jobTitle: string | null;
  department: string | null;
  accountEnabled: boolean;
  createdDateTime: string | null;
  lastSignInDateTime: string | null;
  mfaRegistered: boolean;
  mfaMethods: string[];
  licenses: { skuId: string; skuPartNumber: string; displayName: string }[];
  groupIds: string[];
}

export interface SyncedGroup {
  id: string;
  displayName: string;
  description: string | null;
  mail: string | null;
  mailEnabled: boolean;
  securityEnabled: boolean;
  groupTypes: string[];
  memberCount: number;
  members: { id: string; displayName: string; userPrincipalName: string | null; type: string }[];
}

export interface SyncedSharedMailbox {
  id: string;
  displayName: string;
  email: string;
  alias: string;
  createdDate: string | null;
  memberCount: number;
  members: string[];
  permissions?: { delegate: string; fullAccess: boolean; sendAs: boolean; sendOnBehalf: boolean }[];
}

export interface SyncedUserMailbox {
  email: string;
  displayName: string;
  mailboxType: string;
  totalSizeMB: number | null;
  itemCount: number | null;
  archiveEnabled: boolean;
  archiveSizeMB: number | null;
  retentionPolicy: string | null;
  forwardingAddress: string | null;
  deliverAndForward: boolean;
  autoReplyEnabled: boolean;
  lastLogonTime: string | null;
}

export interface SyncedSharePointSite {
  id: string;
  displayName: string;
  webUrl: string;
  createdDateTime: string | null;
  lastModifiedDateTime: string | null;
}

export interface SyncedSitePermission {
  siteId: string;
  siteUrl: string;
  siteName: string;
  groups: SyncedSitePermissionGroup[];
}

export interface SyncedSitePermissionGroup {
  name: string;
  role: string;
  members: string[];
}

export interface SyncedSecurityInfo {
  secureScore: number | null;
  secureScoreMax: number | null;
  secureScorePercentage: number | null;
  totalUsers: number;
  mfaRegisteredUsers: number;
  mfaCoverage: number;
  globalAdmins: { id: string; displayName: string; userPrincipalName: string; roles: string[]; mfaRegistered: boolean }[];
  otherAdmins: { id: string; displayName: string; userPrincipalName: string; roles: string[]; mfaRegistered: boolean }[];
  totalAdminCount: number;
  conditionalAccessPolicies: { id: string; displayName: string; state: string; createdDateTime: string | null; modifiedDateTime: string | null }[];
  staleAccountCount: number;
  guestUserCount: number;
}

// Google Workspace Synced Data Types
export interface SyncedGoogleOAuthApp {
  appId: string;
  appName: string;
  userEmail: string;
  userName: string;
  scopes: string[];
  authorizedDate: string | null;
  isPersonalApp: boolean;  // Flagged as consumer/personal app
}

export interface SyncedGoogleMobileDevice {
  deviceId: string;
  userEmail: string;
  userName: string;
  model: string;
  os: string;
  osVersion: string;
  type: string;  // 'android' | 'ios' | 'other'
  status: string;  // 'approved' | 'pending' | 'blocked'
  lastSync: string | null;
  isEndOfLife: boolean;  // OS version flagged as EOL
}

export interface SyncedGoogleAdminAuditEntry {
  id: string;
  timestamp: string;
  adminEmail: string;
  adminName: string;
  action: string;
  target: string;
  details: string;
  isSensitive: boolean;  // password changes, role changes, user creation/deletion
}

export interface ClientSyncData {
  id: string;
  clientId: string;
  clientName: string;
  tenantId: string;
  tenantDomain: string | null;
  syncStatus: OverallSyncStatus;
  lastFullSyncStarted: string | null;
  lastFullSyncCompleted: string | null;
  lastSyncError: string | null;
  lastSyncDurationMs: number | null;
  users: SyncCategory<SyncedUser>;
  groups: SyncCategory<SyncedGroup>;
  sharedMailboxes: SyncCategory<SyncedSharedMailbox>;
  userMailboxes?: SyncCategory<SyncedUserMailbox>;
  sharePointSites?: SyncCategory<SyncedSharePointSite>;
  sitePermissions?: SyncCategory<SyncedSitePermission>;
  securityInfo: SyncCategorySingle<SyncedSecurityInfo>;
  // Google Workspace categories (only populated for google/hybrid clients)
  googleOAuthApps?: SyncCategory<SyncedGoogleOAuthApp>;
  googleMobileDevices?: SyncCategory<SyncedGoogleMobileDevice>;
  googleAdminAudit?: SyncCategory<SyncedGoogleAdminAuditEntry>;
  syncSchedule: SyncSchedule;
  createdDate: string;
  modifiedDate: string;
}

export interface GetSyncDataResponse {
  success: boolean;
  data: ClientSyncData | null;
  needsSync: boolean;
  staleness: Staleness;
}

export interface TriggerSyncResponse {
  success: boolean;
  clientId: string;
  clientName: string;
  syncStatus: OverallSyncStatus;
  durationMs: number;
  summary: {
    users: { count: number; success: boolean };
    groups: { count: number; success: boolean };
    sharedMailboxes: { count: number; success: boolean };
    security: { success: boolean };
  };
}
